/**
 * The subset of handlers that is safe to run in a Worker (§30, §33).
 *
 * §33 asks that parsing a large or hostile file never freeze the interface, and
 * §30 asks that a malicious file be contained. Both point at the same
 * mechanism: run the parse off the UI thread, in something that can be
 * terminated. A Worker is that something.
 *
 * Not every handler can go there, and the boundary is drawn by two questions:
 *
 * - **Does its model survive `structuredClone`?** A Worker returns data, not
 *   objects with methods or live handles. PDF is excluded because its model is
 *   built on a pdf.js document proxy that cannot cross the boundary; images are
 *   excluded because decoding leans on the host's decoder and canvas.
 * - **Does it pull a renderer-only dependency?** This module deliberately
 *   imports each handler from its own path rather than from the package index,
 *   so the Worker's bundle never contains `pdf-lib` or the image code — a
 *   Worker has no `document`, and importing those can fail at load.
 *
 * The formats that remain — the document, spreadsheet, presentation and audio
 * parsers — are exactly the ones whose parse is heavy enough for §33 to care
 * about, so the boundary keeps the expensive work and leaves the cheap,
 * renderer-bound work where it already runs.
 */
import { HandlerRegistry } from './registry.js';
import { textHandler } from './text/handler.js';
import { csvHandler } from './csv/handler.js';
import { xlsxHandler } from './xlsx/handler.js';
import { docxHandler } from './docx/handler.js';
import { pptxHandler } from './pptx/handler.js';
import { hwpxHandler } from './hwpx/handler.js';
import { odtHandler } from './odf/text-handler.js';
import { odsHandler } from './odf/sheet-handler.js';
import { odpHandler } from './odf/presentation-handler.js';
import { rtfHandler } from './rtf/handler.js';
import { audioHandler } from './media/handler.js';
import { videoHandler } from './media/video-handler.js';
import { hwpHandler } from './hwp/handler.js';
import { heicHandler } from './image/heic-handler.js';
import { epubHandler } from './epub/handler.js';

/**
 * Format ids whose parse runs in the Worker. The shell routes exactly these to
 * the Worker and keeps everything else — PDF, images, and the cheap archive
 * directory read — on the main thread.
 */
export const WORKER_SAFE_FORMATS: ReadonlySet<string> = new Set([
  'txt', 'markdown', 'csv', 'tsv',
  'xlsx', 'xlsm', 'docx', 'pptx', 'hwpx',
  'odt', 'ods', 'odp', 'rtf', 'hwp',
  'wav', 'mp3', 'mp4', 'mov', 'webm', 'heic', 'epub',
]);

/** Build a registry holding only the Worker-safe handlers. */
export function createWorkerSafeRegistry(): HandlerRegistry {
  return new HandlerRegistry()
    .register(textHandler)
    .register(csvHandler)
    .register(xlsxHandler)
    .register(docxHandler)
    .register(pptxHandler)
    .register(hwpxHandler)
    .register(odtHandler)
    .register(odsHandler)
    .register(odpHandler)
    .register(rtfHandler)
    .register(audioHandler)
    .register(videoHandler)
    .register(hwpHandler)
    .register(heicHandler)
    .register(epubHandler);
}
